import { useEffect, useRef, useState } from 'react'
import o from '../components/Overlay.module.css'
import pr from './personnel/PersonRecordPanel.module.css'
import be from './personnel/BulkEditPanel.module.css'
import { useAuth } from '../contexts/AuthContext'
import { useLayer } from '../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../components/pending'
import { apiJson } from '../lib/api'
import { TriState, type Tri } from './personnel/BulkEditPanel'
import type { Destination, Group } from './GroupsPage'

// Bulk edit for ticked groups (the Personnel pattern): what makes sense for
// many groups at once, Timesheet capture, each destination switched on or off,
// and archive. A name, a manager and members belong to one group, so they stay
// in its own panel. Every field starts "Unchanged"; the server half is
// POST /api/groups/bulk, which changes all of them or none.

const CHIP_CAP = 8
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

interface Props {
  groups: Group[]
  onClose: () => void
  onSaved: (message: string) => void
}

export function GroupBulkPanel({ groups, onClose, onSaved }: Props) {
  const { user } = useAuth()
  const n = groups.length
  const [options, setOptions] = useState<Destination[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [capture, setCapture] = useState<Tri>('unchanged')
  const [dest, setDest] = useState<Record<string, Tri>>({})
  const [archive, setArchive] = useState<Tri>('unchanged')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const slow = useSlow(busy)

  useEffect(() => {
    let live = true
    apiJson<Destination[]>(user, '/api/groups/destinations')
      .then((r) => live && setOptions(Array.isArray(r) ? r : []))
      .catch((e: any) => live && setLoadError(e?.message || 'Could not load the destinations'))
    return () => {
      live = false
    }
  }, [])

  const onCount = (id: string) => groups.filter((g) => (g.destinations || []).some((d) => d.id === id)).length
  const addDest = Object.keys(dest).filter((k) => dest[k] === 'on')
  const removeDest = Object.keys(dest).filter((k) => dest[k] === 'off')
  const label = (id: string) => options?.find((x) => x.id === id)?.label || id

  const changes: string[] = []
  if (capture !== 'unchanged') changes.push(`Timesheet capture ${capture}`)
  addDest.forEach((d) => changes.push(`${label(d)} on`))
  removeDest.forEach((d) => changes.push(`${label(d)} off`))
  if (archive !== 'unchanged') changes.push(archive === 'on' ? 'archive' : 'bring back')

  // What the server would refuse, said here first: the default group is never
  // archived, nor a group with people in it, and a destination can only be
  // newly switched on while it is connected.
  const missing: string[] = []
  if (archive === 'on') {
    const def = groups.find((g) => g.is_default)
    if (def) missing.push(`untick ${def.name}, the default group is never archived`)
    const full = groups.filter((g) => !g.is_archived && !g.is_default && g.member_count > 0)
    if (full.length) missing.push(`move the people out of ${full.map((g) => g.name).join(', ')}`)
  }
  for (const d of addDest) {
    const opt = options?.find((x) => x.id === d)
    if (opt && opt.status !== 'connected' && onCount(d) < n) missing.push(`connect ${opt.label} in Integrations`)
  }
  const blocked = missing.length > 0

  const panelEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, () => !busy && onClose(), { dirty: changes.length > 0 && !busy, el: panelEl, modal: true, focusFirst: true, busy })

  const save = async () => {
    if (blocked || busy || !changes.length) return
    setBusy(true)
    setSaveError(null)
    const body: Record<string, unknown> = { ids: groups.map((g) => g.id) }
    if (capture !== 'unchanged') body.timesheetCapture = capture === 'on'
    if (addDest.length) body.addDestinations = addDest
    if (removeDest.length) body.removeDestinations = removeDest
    if (archive !== 'unchanged') body.isArchived = archive === 'on'
    try {
      const r = await apiJson<{ updated: number }>(user, '/api/groups/bulk', { body })
      onSaved(`${plural(r.updated, 'group', 'groups')} updated: ${changes.join(', ')}`)
    } catch (e: any) {
      setSaveError(e?.message || 'Nothing was changed.')
      setBusy(false)
    }
  }

  return (
    <div className={o.drawerScrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={panelEl} tabIndex={-1} className={`${o.drawer} ${be.drawer}`} role="dialog" aria-label={`Edit ${n} groups`}>
        <div className={o.drawerHead}>
          <div className={o.headText}>
            <h2 className={o.drawerTitle}>Edit {n} groups</h2>
            <p className={o.drawerSub}>Anything you leave alone stays as it is on each group. Names, managers and members are set on each group.</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={be.chips} aria-label="Selected groups" data-testid="bulk-chips">
          {groups.slice(0, CHIP_CAP).map((g) => (
            <span key={g.id} className={be.chip}>
              {g.name}
            </span>
          ))}
          {n > CHIP_CAP && <span className={be.more}>and {n - CHIP_CAP} more</span>}
        </div>

        <fieldset className={`${o.drawerBody} ${be.body} ${inertClass(busy, slow)}`} disabled={busy} aria-busy={busy}>
          {loadError && <div className={pr.reason}>{loadError}</div>}

          <div className={`${o.toggleRow} ${be.triRow}`}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Timesheet capture</div>
              <div className={o.toggleSub}>
                {capture === 'on'
                  ? `Workers in all ${n} clock on and off in the app and appear in Timesheets and Pay Workers.`
                  : capture === 'off'
                    ? `Workers in all ${n} come off Timesheets and Pay Workers, hours already recorded this pay period included. Their picks are still recorded and nothing is deleted: switch it back on and they return.`
                    : `Each group keeps its own setting. On now for ${groups.filter((g) => g.timesheet_capture !== false).length} of ${n}.`}
              </div>
            </div>
            <TriState label="Timesheet capture" value={capture} options={['On', 'Off']} onChange={setCapture} />
          </div>

          {!options
            ? !loadError && <div className={o.fieldHint}>Loading destinations…</div>
            : options.map((d) => {
                const v = dest[d.id] || 'unchanged'
                return (
                  <div key={d.id} className={`${o.toggleRow} ${be.triRow}`}>
                    <div className={o.toggleText}>
                      <div className={o.toggleTitle}>{d.label}</div>
                      <div className={o.toggleSub}>
                        {v === 'on' ? `All ${n} send to ${d.label}.` : v === 'off' ? `None of the ${n} send to ${d.label}.` : `On now for ${onCount(d.id)} of ${n}.`}
                        {d.status !== 'connected' && ` ${d.label} is not connected, so it can only be switched off here.`}
                      </div>
                    </div>
                    <TriState label={d.label} value={v} options={['On', 'Off']} onChange={(x) => setDest({ ...dest, [d.id]: x })} />
                  </div>
                )
              })}

          <div className={`${o.toggleRow} ${be.triRow}`}>
            <div className={o.toggleText}>
              <div className={o.toggleTitle}>Archive</div>
              <div className={o.toggleSub}>
                {archive === 'on'
                  ? `All ${n} move to the Archived tab, keeping every past timesheet. Only a group with nobody in it can be archived.`
                  : archive === 'off'
                    ? `All ${n} come back from the Archived tab.`
                    : 'Each group stays where it is.'}
              </div>
            </div>
            <TriState label="Archive" value={archive} options={['Archive', 'Bring back']} onChange={setArchive} />
          </div>
        </fieldset>

        <div className={`${o.drawerFoot} ${pr.foot}`}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <span className={blocked ? o.footNote : `${o.footNote} ${be.changeNote}`} data-testid="bulk-changes">
            {blocked ? `First ${missing.join('; ')}.` : changes.length ? `Changes: ${changes.join(', ')}` : 'Nothing has changed yet'}
          </span>
          <PendingButton className={o.primary} busy={busy} pending="Applying…" disabled={blocked || !changes.length} onClick={save}>
            {`Apply to ${n}`}
          </PendingButton>
          <Failed text={saveError && `${saveError} No group was changed.`} />
        </div>
      </div>
      {confirm}
    </div>
  )
}
